import { t } from 'i18next';
import i18n from '../../../language/i18n';
import user_profile_photo from '../../images/def_user_icon.svg';
import { formatDateLocalized } from '../../../utils/formatDateLocalized';
import { getCurrentBackendLanguage } from '../../../utils/apiErrorHandler';
import { deleteMessage } from '../../../api/post/deleteMessage';
import { hideMessage } from '../../../api/post/hideMessage';
import {
  SPECIAL_SERVICE_IDS,
  MESSAGABLE_SERVICE_IDS,
  READ_ONLY_SERVICE_IDS,
  ANNOUNCEMENT_URL_REGEX,
} from './chatConstants';

export const isNameUnknown = (name) => !name || name.trim() === '' || name.toLowerCase() === 'unknown';

// Persist and restore chat names to localStorage so they survive page refresh
const CHAT_NAMES_KEY = 'chat_partner_names';

export const saveChatNames = (chats, currentUserId) => {
  try {
    const names = {};
    for (const chat of chats) {
      const key = String(chat.room_id);
      const isSentByMe = Number(chat.sender_id) === Number(currentUserId);
      const partnerName = isSentByMe ? chat.receiver_name : chat.sender_name;
      const ownName = isSentByMe ? chat.sender_name : chat.receiver_name;
      const partnerAvatar = resolveChatPartnerAvatar(chat, currentUserId);
  if (!isNameUnknown(partnerName) || !isNameUnknown(ownName) || partnerAvatar !== user_profile_photo) {
        names[key] = {
          partnerName: isNameUnknown(partnerName) ? undefined : partnerName,
          ownName: isNameUnknown(ownName) ? undefined : ownName,
          sender_name: chat.sender_name,
          receiver_name: chat.receiver_name,
          sender_id: chat.sender_id,
          receiver_id: chat.receiver_id,
          other_user_id: chat.other_user_id,
          other_user_avatar_url: chat.other_user_avatar_url,
          sender_avatar_url: chat.sender_avatar_url,
          receiver_avatar_url: chat.receiver_avatar_url,
          avatar_url: chat.avatar_url,
          profile_picture: chat.profile_picture,
          profilePicture: chat.profilePicture,
        };
      }
    }
    localStorage.setItem(CHAT_NAMES_KEY, JSON.stringify(names));
  } catch (e) {
    // Ignore storage errors
  }
};

export const loadChatNames = () => {
  try {
    const raw = localStorage.getItem(CHAT_NAMES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

export const resolveChatPartnerName = (chat, currentUserId) => {
  if (!chat) return t('select_chat_placeholder');
  const isSentByMe = Number(chat.sender_id) === Number(currentUserId);
  const partnerName = isSentByMe ? chat.receiver_name : chat.sender_name;
  if (!isNameUnknown(partnerName)) return partnerName;
  const fallback = isSentByMe ? chat.sender_name : chat.receiver_name;
  if (!isNameUnknown(fallback)) return fallback;
  // Try localStorage names
  const savedNames = loadChatNames();
  const saved = savedNames[String(chat.room_id)];
  if (saved) {
    const savedPartner = isSentByMe ? saved.receiver_name : saved.sender_name;
    if (!isNameUnknown(savedPartner)) return savedPartner;
    const savedFallback = isSentByMe ? saved.sender_name : saved.receiver_name;
    if (!isNameUnknown(savedFallback)) return savedFallback;
  }
  return t('user');
};

const pickAvatarUrl = (chat) => {
  return chat?.other_user_avatar_url
    || chat?.avatar_url
    || chat?.profile_picture
    || chat?.profilePicture
    || chat?.avatar;
};

export const resolveChatPartnerAvatar = (chat, currentUserId) => {
  const savedNames = loadChatNames();
  const saved = savedNames[String(chat?.room_id)];
  return pickAvatarUrl(chat)
    || (Number(chat?.sender_id) === Number(currentUserId)
        ? (chat?.receiver_avatar_url || saved?.receiver_avatar_url || saved?.avatar_url || saved?.profile_picture)
        : (chat?.sender_avatar_url || saved?.sender_avatar_url || saved?.avatar_url || saved?.profile_picture))
    || chat?.receiver_profile_picture
    || chat?.sender_profile_picture
    || user_profile_photo;
};

export const isSpecialService = (chat) => {
  return SPECIAL_SERVICE_IDS.includes(Number(chat?.sender_id)) || SPECIAL_SERVICE_IDS.includes(Number(chat?.receiver_id));
};

// Сервис чаттың қайсы қызметке тиесілі екенін анықтайды (sender_id немесе
// receiver_id ішінен SPECIAL_SERVICE_IDS мүшесін іздейді). room_id сервер
// жаңартқанда өзгеруі мүмкін, ал қызмет id-і тұрақты → өшірілген сервис чатты
// осы id бойынша сақтаймыз.
export const getServiceId = (chat) => {
  const s = Number(chat?.sender_id);
  const r = Number(chat?.receiver_id);
  if (SPECIAL_SERVICE_IDS.includes(s)) return s;
  if (SPECIAL_SERVICE_IDS.includes(r)) return r;
  return null;
};

// Өшірілген сервис чаттарды localStorage-те сақтаймыз — сервер әр chat-list
// push кезінде сервис чаттарды қайта құрады (applyServerChats оларды қайта қосып
// тастайды), сондықтан клиент жағында өшірілген қызмет id-ін сүзгілейміз.
const DELETED_SERVICE_CHATS_KEY = 'deleted_service_chats';

// Әр чаттың соңғы хабар түрін (message_type) localStorage-те сақтаймыз — бекенд
// chat-list жауабында message_type қайтармаса да, бет қайта жүктелгенде чат-тізім
// соңғы хабар жарнама-карточка екенін бірден көрсетсін деп.
const LAST_MSG_TYPE_KEY = 'chat_last_msg_types';
let lastMsgTypesCache = null;

export const getLastMsgTypes = () => {
  if (lastMsgTypesCache != null) return lastMsgTypesCache;
  try {
    const raw = localStorage.getItem(LAST_MSG_TYPE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    lastMsgTypesCache = obj && typeof obj === 'object' ? obj : {};
  } catch {
    lastMsgTypesCache = {};
  }
  return lastMsgTypesCache;
};

export const getLastMsgType = (roomId) => {
  if (roomId == null) return null;
  return getLastMsgTypes()[String(roomId)] || null;
};

export const setLastMsgType = (roomId, type) => {
  if (roomId == null) return;
  const obj = getLastMsgTypes();
  const key = String(roomId);
  if (type && type !== 'text') {
    obj[key] = type;
  } else {
    delete obj[key];
  }
  try { localStorage.setItem(LAST_MSG_TYPE_KEY, JSON.stringify(obj)); } catch {}
};

export const getDeletedServiceIds = () => {
  try {
    const raw = localStorage.getItem(DELETED_SERVICE_CHATS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set((Array.isArray(arr) ? arr : []).map(Number));
  } catch {
    return new Set();
  }
};

export const addDeletedServiceId = (serviceId) => {
  if (serviceId == null) return;
  const set = getDeletedServiceIds();
  set.add(Number(serviceId));
  try {
    localStorage.setItem(DELETED_SERVICE_CHATS_KEY, JSON.stringify([...set]));
  } catch {}
};

// Серверден келген чат тізімінен өшірілген сервис чаттарды алып тастайды.
// Ештеңе өшірілмеген болса (set бос) — тізімді өзгертпейді (қосымша шығын жоқ).
export const filterDeletedServiceChats = (chats) => {
  const deleted = getDeletedServiceIds();
  if (deleted.size === 0) return chats;
  return chats.filter((chat) => {
    const sid = getServiceId(chat);
    return sid === null || !deleted.has(Number(sid));
  });
};

export const isMessagableService = (chat) => {
  return MESSAGABLE_SERVICE_IDS.includes(Number(chat?.sender_id)) || MESSAGABLE_SERVICE_IDS.includes(Number(chat?.receiver_id));
};

export const isReadOnlyChat = (chat) => {
  return READ_ONLY_SERVICE_IDS.includes(Number(chat?.sender_id)) || READ_ONLY_SERVICE_IDS.includes(Number(chat?.receiver_id));
};

export const generateFakeRoomId = (userId1, userId2) => {
  return `chat_${Math.min(userId1, userId2)}_${Math.max(userId1, userId2)}`;
};

export const isFakeRoomId = (roomId) => typeof roomId === 'string' && roomId.startsWith('chat_');

// Индекске тәуелсіз тұрақты кілт — MessageBubble (күн тобы ішіндегі индекс) мен
// selectAll/REMOVE_MESSAGES (жалпы индекс) бір хабарламаға бірдей кілт береді.
export const getMessageId = (msg) => {
  const serverId = msg.message_id ?? msg.id;
  if (serverId != null) return String(serverId);
  if (msg.tempId != null) return String(msg.tempId);
  return `${msg.sender_id}_${msg.timestamp}`;
};

// Хабарламаларды серверден бір арна арқылы өшіреміз/жасырамыз: socket қосулы
// болса — socket оқиғасы, әйтпесе REST. Өз хабарламасы — delete_message (екі
// қатысушыда да өшеді), бөтені — hide_message («мен үшін», барлық құрылғымда).
export const deleteMessagesForRoom = (messages, { socket, currentRoomName, currentUserId }) => {
  const useSocket = !!(socket?.connected && currentRoomName);
  messages.forEach(msg => {
    const serverId = msg.message_id ?? msg.id;
    if (serverId == null) return;
    const isOwn = Number(msg.sender_id) === Number(currentUserId);
    if (useSocket) {
      if (isOwn) {
        socket.emit("delete_message", { message_id: serverId, room_name: currentRoomName, language: getCurrentBackendLanguage() });
      } else {
        socket.emit("hide_message", { message_id: serverId });
      }
      return;
    }
    (isOwn ? deleteMessage(serverId) : hideMessage(serverId)).catch(err => {
      console.error('[Chat] Failed to delete message:', serverId, err);
    });
  });
};

export const isDuplicateMessage = (existingMessages, newMessage) => {
  return existingMessages.some(
    (msg) =>
      Number(msg.sender_id) === Number(newMessage.sender_id) &&
      msg.message === newMessage.message &&
      Math.abs(new Date(msg.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 1000
  );
};

export const groupMessagesByDate = (msgs) => {
  return msgs.reduce((groups, message) => {
    const messageDate = new Date(message.timestamp);
    if (isNaN(messageDate.getTime())) return groups;
    const isoKey = messageDate.toISOString().split('T')[0];
    if (!groups[isoKey]) groups[isoKey] = [];
    groups[isoKey].push(message);
    return groups;
  }, {});
};

export const formatDateHeader = (isoDateKey) => {
  const [y, m, d] = (isoDateKey || '').split('-').map(Number);
  const messageDate = new Date(y, (m || 1) - 1, d || 1);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const daysOfWeek = t('daysOfWeek', { returnObjects: true });
  const months = t('months', { returnObjects: true });

  if (messageDate.toDateString() === today.toDateString()) return t('today');
  if (messageDate.toDateString() === yesterday.toDateString()) return t('yesterday');
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  if (messageDate >= weekStart) return daysOfWeek[messageDate.getDay()];
  if (messageDate.getFullYear() === today.getFullYear()) return `${messageDate.getDate()} ${months[messageDate.getMonth()]}`;
  return isoDateKey;
};

export const formatRecordingDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const isAnnouncementUrl = (messageText) => ANNOUNCEMENT_URL_REGEX.test(messageText);

// Координатты мекенжай атауына айналдырады (OpenStreetMap Nominatim). Локация
// хабарын өздікті қылу үшін — атауымен бірге жіберілсін, мобилка да өзі
// geocode жасамай-ақ көрсете алады. Сәтсіз болса null қайтарады.
export const reverseGeocode = async (lat, lng, timeoutMs = 4000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=kk,ru,en`,
      { headers: { 'User-Agent': 'AgrolandApp/1.0' }, signal: controller.signal }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.display_name || data.address?.city || data.address?.town || data.address?.village || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

export const highlightText = (text, query) => {
  if (!query || !query.trim()) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">{part}</mark>
      : part
  );
};

const TIME_LOCALE_MAP = {
  kz: 'kk-KZ',
  ru: 'ru-RU',
  en: 'en-US',
  cn: 'zh-CN',
};

export const formatTimestamp = (timestamp) => {
  if (timestamp == null || timestamp === '') return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const locale = TIME_LOCALE_MAP[i18n.language] || 'kk-KZ';
  let formatted = '';
  try {
    formatted = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  } catch {}
  // Кейбір браузерлерде (Safari/WebView) kk-KZ локалінің уақыт қалыбы жоқ —
  // toLocaleTimeString бос жол қайтарады. Онда қолмен, жергілікті уақытпен
  // HH:mm құраймыз (getUTCHours емес — ол UTC беріп, 5 сағат артта көрсететін).
  if (formatted && !/invalid/i.test(formatted)) return formatted;
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

// «Соңғы рет қашан кірді» (last seen) үшін relative-time форматы.
// lastOnline — ISO timestamp (бекенд user_offline оқиғасынан / профилден).
export const formatLastSeen = (lastOnline, tFn = t) => {
  if (!lastOnline) return '';
  const date = new Date(lastOnline);
  if (isNaN(date.getTime())) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSec < 60) return tFn('last_seen_just_now');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return tFn('last_seen_minutes', { count: diffMin });
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return tFn('last_seen_hours', { count: diffH });
  const diffD = Math.floor(diffH / 24);
  if (diffD < 2) return tFn('last_seen_yesterday');
  if (diffD < 7) return tFn('last_seen_days', { count: diffD });
  return tFn('last_seen_date', { date: formatDateLocalized(lastOnline, tFn) });
};

export const mergeChatNames = (serverChats, localChats) => {
  const localMap = {};
  for (const c of localChats) {
    localMap[c.room_id] = c;
  }
  const localByParticipants = {};
  for (const c of localChats) {
    if (c.sender_id != null && c.receiver_id != null) {
      const key = `${Math.min(Number(c.sender_id), Number(c.receiver_id))}_${Math.max(Number(c.sender_id), Number(c.receiver_id))}`;
      localByParticipants[key] = c;
    }
  }
  const localByOtherUser = {};
  for (const c of localChats) {
    if (c.other_user_id != null) {
      localByOtherUser[String(c.other_user_id)] = c;
    }
  }
  // Also load saved names from localStorage for page refresh persistence
  const savedNames = loadChatNames();

  return serverChats.map(chat => {
    let local = localMap[chat.room_id];
    if (!local && chat.sender_id != null && chat.receiver_id != null) {
      const pKey = `${Math.min(Number(chat.sender_id), Number(chat.receiver_id))}_${Math.max(Number(chat.sender_id), Number(chat.receiver_id))}`;
      local = localByParticipants[pKey];
    }
    if (!local && chat.other_user_id != null) {
      local = localByOtherUser[String(chat.other_user_id)];
    }
    // If no local match, try localStorage
    const saved = savedNames[String(chat.room_id)];
    if (!local && saved) {
      local = saved;
    }
    if (!local) return chat;
    const localAvatar = pickAvatarUrl(local);
    return {
      ...chat,
      sender_name: isNameUnknown(chat.sender_name) ? (local.sender_name || chat.sender_name) : chat.sender_name,
      receiver_name: isNameUnknown(chat.receiver_name) ? (local.receiver_name || chat.receiver_name) : chat.receiver_name,
      other_user_id: chat.other_user_id ?? local.other_user_id,
      sender_id: chat.sender_id ?? local.sender_id,
      receiver_id: chat.receiver_id ?? local.receiver_id,
      other_user_avatar_url: chat.other_user_avatar_url || local.other_user_avatar_url,
      sender_avatar_url: chat.sender_avatar_url || local.sender_avatar_url,
      receiver_avatar_url: chat.receiver_avatar_url || local.receiver_avatar_url,
      avatar_url: chat.avatar_url || local.avatar_url || local.profile_picture || local.profilePicture,
      profile_picture: chat.profile_picture || local.profile_picture || local.profilePicture,
    };
  });
};

export const findChatByParticipants = (chatList, currentUserId, otherUserId) => {
  return chatList.find(chat => {
    // Қызмет/бот/оператор чаттарын жеке чат іздегенде елемейміз —
    // жарнамадан хабарласқанда қызмет чатына құламасын.
    if (isSpecialService(chat)) return false;
    // Try sender_id/receiver_id matching
    if (chat.sender_id != null && chat.receiver_id != null) {
      if ((Number(chat.sender_id) === Number(currentUserId) && Number(chat.receiver_id) === Number(otherUserId)) ||
          (Number(chat.sender_id) === Number(otherUserId) && Number(chat.receiver_id) === Number(currentUserId))) {
        return true;
      }
    }
    // Try other_user_id matching
    if (chat.other_user_id != null && Number(chat.other_user_id) === Number(otherUserId)) {
      return true;
    }
    return false;
  });
};

export const isChatMatch = (chat, data) => {
  if (data.room_id && chat.room_id) {
    if (String(chat.room_id) === String(data.room_id)) return true;
  }
  if (chat.sender_id != null && chat.receiver_id != null && data.sender_id != null && data.receiver_id != null) {
    if ((Number(chat.sender_id) === Number(data.sender_id) && Number(chat.receiver_id) === Number(data.receiver_id)) ||
        (Number(chat.sender_id) === Number(data.receiver_id) && Number(chat.receiver_id) === Number(data.sender_id))) {
      return true;
    }
  }
  // Match by other_user_id
  if (chat.other_user_id != null && data.other_user_id != null && Number(chat.other_user_id) === Number(data.other_user_id)) {
    return true;
  }
  return false;
};

export const createOptimisticMessage = (message, messageType, currentUserId) => {
  return {
    tempId: Date.now() + Math.random(),
    message,
    message_type: messageType,
    sender_id: currentUserId,
    sender_name: t('you'),
    timestamp: new Date().toISOString(),
    is_read: false,
    isRead: false,
  };
};

export const isPhotoMessage = (msg) => msg?.message_type === 'image' && !!msg?.file_url;

// Фото хабарының подписі: подпись жоқ болса message = file_url (ескі клиенттермен үйлесімділік)
export const getPhotoCaption = (msg) => {
  const text = msg?.message || '';
  return text && text !== msg?.file_url ? text : '';
};
